import React, { useRef, useState, useEffect } from 'react';
import { uploadFile, listSources, previewSource, deleteSource, errMsg } from './aiPaperApi';
import { ui } from './ui';
import TextDrawer from './TextDrawer';

const MAX_MB = 50;

function UploadStep({ user, source, onSourceReady }) {
  const [drag, setDrag] = useState(false);
  const [pct, setPct] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | uploading | extracting
  const [error, setError] = useState('');
  const [recent, setRecent] = useState([]);
  const [drawer, setDrawer] = useState(null); // { loading, data, error }
  const inputRef = useRef(null);

  useEffect(() => {
    // chapters only: sample papers / images are handled by the style panel
    listSources(user).then((l) => setRecent(l.filter((d) => d.fileType !== 'image' && d.category !== 'style_sample'))).catch(() => {});
  }, [user, source]);

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return setError('Please choose a PDF file.');
    if (file.size > MAX_MB * 1024 * 1024) return setError(`File is larger than ${MAX_MB}MB.`);
    setPhase('uploading');
    setPct(0);
    try {
      const doc = await uploadFile(user, file, { category: 'syllabus', subject: user?.subject || '', class: user?.class || '' }, (p) => {
        setPct(p);
        if (p >= 100) setPhase('extracting');
      });
      onSourceReady(doc);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setPhase('idle');
    }
  };

  const openDrawer = async (id) => {
    setDrawer({ loading: true });
    try {
      setDrawer({ loading: false, data: await previewSource(user, id) });
    } catch (e) {
      setDrawer({ loading: false, error: errMsg(e) });
    }
  };

  const removeRecent = async (d) => {
    if (!window.confirm('Delete this source PDF?')) return;
    try {
      await deleteSource(user, d._id);
      setRecent((r) => r.filter((x) => x._id !== d._id));
      if (source?._id === d._id) onSourceReady(null);
    } catch (e) { setError(errMsg(e)); }
  };

  const busy = phase !== 'idle';
  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        style={{ ...ui.dropzone, borderColor: drag ? '#4f46e5' : '#a5b4fc', background: drag ? '#eef2ff' : '#f8faff' }}
      >
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
          onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }} />
        <div style={{ fontSize: 44 }}>📚</div>
        <div style={{ fontWeight: 800, fontSize: 17, color: '#1e1b4b' }}>Drag & drop NCERT / textbook chapter PDF</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>or click to browse · PDF only · up to {MAX_MB}MB · text-based PDFs (no scans)</div>
      </div>

      {busy && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3730a3', marginBottom: 6 }}>
            {phase === 'uploading' ? `Uploading… ${pct}%` : 'Extracting chapter text… (large PDFs can take a minute)'}
          </div>
          <div style={ui.progressTrack}>
            <div style={{ ...ui.progressBar, width: phase === 'uploading' ? `${pct}%` : '100%', opacity: phase === 'extracting' ? 0.7 : 1 }} />
          </div>
        </div>
      )}

      {error && <div style={ui.errorBox}>{error}</div>}

      {source && !busy && (
        <div style={{ ...ui.okBox, marginTop: 14 }}>
          <div style={{ fontWeight: 800 }}>✅ {source.filename}</div>
          <div style={{ fontSize: 13, marginTop: 3 }}>
            {source.totalPages} pages{source.chunkCount ? ` · ${source.chunkCount} text chunks` : ''}
            {source.characters ? ` · ${source.characters.toLocaleString()} characters` : ''}
          </div>
          {source.extractionStatus === 'low_text' && <div style={{ ...ui.warnText, marginTop: 4 }}>⚠️ {source.extractionNote}</div>}
          <button style={{ ...ui.btnGhost, marginTop: 8 }} onClick={() => openDrawer(source._id)}>🔍 Verify extracted text</button>
        </div>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={ui.label}>Previously uploaded chapters</div>
          {recent.slice(0, 8).map((d) => (
            <div key={d._id} style={ui.listRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.originalName || d.filename}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{d.totalPages} pages · {new Date(d.uploadedAt).toLocaleDateString('en-IN')}</div>
              </div>
              <button style={ui.btnSmall} onClick={() => onSourceReady({ ...d, filename: d.originalName || d.filename })}>Use</button>
              <button style={ui.btnSmall} onClick={() => openDrawer(d._id)}>View</button>
              <button style={{ ...ui.btnSmall, color: '#b91c1c' }} onClick={() => removeRecent(d)}>🗑</button>
            </div>
          ))}
        </div>
      )}

      <TextDrawer drawer={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}

export default UploadStep;
